import { useEffect, useMemo, useRef, useState } from "react";
import type { Flow, BlockDef, EventBinding } from "@cogo/types-v2";
import {
  AlertCircle,
  CheckCircle2,
  GitBranch,
  GitFork,
  Layers,
  MousePointer2,
  Plus,
  Repeat,
  ShieldCheck,
  X,
  Zap,
} from "lucide-react";
import { EditorShell } from "@/features/flow-editor/components/shell/editor-shell";
import { useStudioStore } from "@/store/studio-store";
import { useEditorStore } from "@/features/page-editor/store/editor-store";
import { findElementInTree } from "@/features/page-editor/store/canvas-slice";
import { useFlowEditorStore } from "@/features/flow-editor/store/flow-editor-store";
import { CreateFlowForm } from "@/features/flow-editor/components/create-flow-form";
import { VerificationStatusBadge } from "@/features/flow-editor/components/inspector/verification-status-badge";
import { deriveFlowVerification } from "@/features/flow-editor/lib/flow-verification";
import {
  flowToCanvas,
  normalizeFlowTrigger,
} from "@/features/flow-editor/services/flow-bridge";
import { flowNamesMatch } from "@/features/flow-editor/services/flow-ownership";
import { usePreviewTestingStore } from "@/features/preview/store/preview-testing-store";
import { buildEventHierarchy } from "@/features/flow-editor/services/event-hierarchy";
import type { FlowMeta } from "@/features/flow-editor/models/flow-models";
import { cn } from "@/lib/utils";
import { summarizeActionFlowExecution } from "@/features/flow-editor/services/actionflow-execution-model";
import { useStudioSelectionContext } from "../selection-context";
import { AskCogoSurfaceButton } from "../components/ask-cogo-surface-button";
import {
  StudioEditorHost,
  StudioEditorToolbar,
} from "../workspace/studio-editor-host";
import { useStudioLocale } from "@/i18n";
import { getDataSurfaceCopy } from "@/features/data-explorer/data-surface-copy";

interface BehaviorFlowEntry {
  id: string;
  name: string;
  blockName: string | null;
  trigger: FlowMeta["trigger"];
  flow: Flow;
}

interface BehaviorEventEntry {
  id: string;
  blockName: string;
  elementId: string;
  eventType: string;
  flowName: string;
  actionName: string;
  bindingKind: "flow" | "action";
  status: "spec" | "user" | "orphan";
  binding: EventBinding;
}

interface BehaviorSummary {
  flows: BehaviorFlowEntry[];
  events: BehaviorEventEntry[];
  orphanCount: number;
  emptyFlowCount: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeTrigger(trigger: unknown): FlowMeta["trigger"] {
  return normalizeFlowTrigger(trigger);
}

function unwrapFlowMap(value: unknown): Record<string, Flow> {
  if (!isRecord(value)) return {};
  const rawMap = isRecord(value.flows) ? value.flows : value;
  const flows: Record<string, Flow> = {};
  for (const [name, maybeFlow] of Object.entries(rawMap)) {
    if (!isRecord(maybeFlow)) continue;
    if (
      !Array.isArray(maybeFlow.steps) &&
      typeof maybeFlow.trigger !== "string"
    )
      continue;
    flows[name] = {
      ...(maybeFlow as unknown as Flow),
      name: (maybeFlow.name as string | undefined) ?? name,
      trigger: (maybeFlow.trigger as Flow["trigger"] | undefined) ?? "event",
      steps: Array.isArray(maybeFlow.steps) ? maybeFlow.steps : [],
    };
  }
  return flows;
}

function collectBehaviorSummary(
  project: {
    manifest?: unknown;
    blocks?: Record<string, BlockDef>;
    flows?: Record<string, Flow>;
  } | null,
): BehaviorSummary {
  const flows: BehaviorFlowEntry[] = [];
  const flowIds = new Set<string>();
  const flowNames = new Set<string>();
  const addFlow = (name: string, flow: Flow, blockName: string | null) => {
    const id = blockName ? `${blockName}:${name}` : `project:${name}`;
    const normalizedName = name.toLowerCase();
    if (flowIds.has(id) || flowNames.has(normalizedName)) return;
    flowIds.add(id);
    flowNames.add(normalizedName);
    flows.push({
      id,
      name,
      blockName,
      trigger: normalizeTrigger(flow.trigger),
      flow,
    });
  };

  for (const [blockName, block] of Object.entries(project?.blocks ?? {})) {
    const blockFlows = unwrapFlowMap((block as { flow?: unknown }).flow);
    for (const [name, flow] of Object.entries(blockFlows)) {
      addFlow(name, flow, blockName);
    }
  }

  for (const [name, flow] of Object.entries(project?.flows ?? {})) {
    if (flow) addFlow(name, flow, null);
  }

  const events: BehaviorEventEntry[] = [];
  if (project?.manifest && project.blocks) {
    const tree = buildEventHierarchy(project.manifest as never, project.blocks);
    for (const blockNode of tree.children) {
      for (const eventNode of blockNode.children) {
        if (
          !eventNode.blockName ||
          !eventNode.elementId ||
          !eventNode.eventType
        )
          continue;
        events.push({
          id: eventNode.id,
          blockName: eventNode.blockName,
          elementId: eventNode.elementId,
          eventType: eventNode.eventType,
          flowName: eventNode.flowName ?? "",
          actionName: eventNode.actionName ?? "",
          bindingKind: eventNode.bindingKind ?? "flow",
          status: eventNode.status ?? "user",
          binding:
            eventNode.binding ??
            ({
              flow: eventNode.flowName,
              params: eventNode.params,
            } as EventBinding),
        });
      }
    }
  }

  return {
    flows,
    events,
    orphanCount: events.filter(
      (event) =>
        event.status === "orphan" || (!event.flowName && !event.actionName),
    ).length,
    emptyFlowCount: flows.filter(
      (entry) =>
        !Array.isArray(entry.flow.steps) || entry.flow.steps.length === 0,
    ).length,
  };
}

function useHydrateBehaviorFlows(
  projectId: string | null,
  summary: BehaviorSummary,
) {
  const syncKey = useMemo(
    () =>
      `${projectId ?? "none"}:${summary.flows.map((flow) => `${flow.id}:${flow.flow.steps?.length ?? 0}`).join("|")}`,
    [projectId, summary.flows],
  );

  useEffect(() => {
    if (!projectId) return;
    // Scope the singleton flow-editor store to THIS project before merging
    // editor-only drafts. Project open/close paths already call this, but a
    // switch that bypasses them must not let the previous project's flows be
    // inferred as "editor-only drafts" of this one (cross-project leak: list
    // and pill pollution, and a write path — one edit would persist the
    // foreign flow into this project's first block). No-op when unchanged.
    useFlowEditorStore.getState().ensureProject(projectId);
    if (summary.flows.length === 0) return;
    const store = useFlowEditorStore.getState();
    const existingIds = new Set(store.flows.map((flow) => flow.id));

    // ID-PRESERVING hydration (COR-1/PI-2): when a persisted entry
    // name-matches a live FlowMeta — the first save of a created flow derives
    // a NEW summary id (`flow_<ts>` → `<block>:<name>`), and a rename derives
    // `<block>:<newName>` while the live meta keeps `<block>:<oldName>` —
    // KEEP the live meta's id and refresh name/trigger/narrative from the
    // persisted entry. Undo inverses (editFlowSteps/renameFlow/
    // restoreFlowSteps) are keyed by flowId and validated against the live
    // flows list on bypass execution: dropping + recreating the meta under
    // the derived id invalidated every prior inverse and wedged the undo
    // stack at the poisoned entry. Only entries with NO matching live meta
    // get the derived `<block>:<name>` id (reload — empty store — still
    // derives every id). Name matching applies flowNamesMatch in BOTH
    // directions: either side may be the more namespaced one.
    const persistedPairs = summary.flows.map((entry) => {
      const narrative =
        (entry.flow as { narrative?: FlowMeta["narrative"] }).narrative ?? null;
      const live =
        store.flows.find((flow) => flow.id === entry.id) ??
        store.flows.find(
          (flow) =>
            flowNamesMatch(flow.name, entry.name) ||
            flowNamesMatch(entry.name, flow.name),
        );
      const meta: FlowMeta = live
        ? { ...live, name: entry.name, trigger: entry.trigger, narrative }
        : { id: entry.id, name: entry.name, trigger: entry.trigger, narrative };
      return { entry, meta };
    });
    const persistedFlows = persistedPairs.map((pair) => pair.meta);

    // Flows that exist only in the editor store (created via New Flow, not
    // yet persisted into any block) survive hydration. Replacing the list
    // wholesale dropped them — and deselected the flow mid-edit (QA-2/QA-5).
    // Name matching uses the ownership full-then-bare rule (flowNamesMatch)
    // so a namespaced editor flow that persists under its bare key is not
    // kept as a phantom duplicate.
    const persistedIds = new Set(persistedFlows.map((flow) => flow.id));
    const editorOnlyFlows = store.flows.filter(
      (flow) =>
        !persistedIds.has(flow.id) &&
        !persistedFlows.some((persisted) =>
          flowNamesMatch(flow.name, persisted.name),
        ),
    );
    const nextFlows = [...persistedFlows, ...editorOnlyFlows];

    // Hydrate when a persisted flow has no live meta yet, or when a preserved
    // live meta needs its name/trigger/narrative refreshed from persistence.
    const liveById = new Map(store.flows.map((flow) => [flow.id, flow]));
    const refreshNeeded = persistedPairs.some(({ meta }) => {
      const live = liveById.get(meta.id);
      return (
        live != null &&
        (live.name !== meta.name ||
          live.trigger !== meta.trigger ||
          JSON.stringify(live.narrative ?? null) !==
            JSON.stringify(meta.narrative ?? null))
      );
    });
    const shouldHydrate =
      persistedFlows.some((flow) => !existingIds.has(flow.id)) || refreshNeeded;
    if (!shouldHydrate && store.flows.length >= nextFlows.length) return;

    // Canvas projections land under the PRESERVED id — flowDataMap (and the
    // selectFlow restore path) is keyed by FlowMeta id, not the derived one.
    const flowDataMap = { ...store.flowDataMap };
    for (const { entry, meta } of persistedPairs) {
      flowDataMap[meta.id] = flowToCanvas(entry.flow, entry.name);
    }

    // Selection: keep by id; else remap by name. With id-preserving hydration
    // the selected meta normally survives under its own id, so this remap is
    // a safety net (e.g. an editor-only draft dropped by the name-match
    // dedupe above must hand its selection to the persisted entry).
    const prevSelectedId = store.selectedFlowId;
    let nextSelectedId: string | null = null;
    if (prevSelectedId) {
      if (nextFlows.some((flow) => flow.id === prevSelectedId)) {
        nextSelectedId = prevSelectedId;
      } else {
        const prevMeta = store.flows.find((flow) => flow.id === prevSelectedId);
        const renamed = prevMeta
          ? nextFlows.find((flow) => flowNamesMatch(prevMeta.name, flow.name))
          : undefined;
        nextSelectedId = renamed?.id ?? null;
      }
    }

    useFlowEditorStore.setState({
      flows: nextFlows,
      flowDataMap,
      selectedFlowId: nextSelectedId,
      nodes: nextSelectedId != null ? store.nodes : [],
      connections: nextSelectedId != null ? store.connections : [],
      // Re-key the editing buffer across the id migration so FlowStepPanel's
      // no-op re-seed detection keeps step selection intact (QA-3).
      ...(nextSelectedId != null &&
      nextSelectedId !== prevSelectedId &&
      store.flowStepsFlowId === prevSelectedId
        ? { flowStepsFlowId: nextSelectedId }
        : {}),
    });
  }, [projectId, syncKey, summary.flows]);
}

function StatusPill({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "ok" | "warn";
}) {
  const Icon = tone === "warn" ? AlertCircle : CheckCircle2;
  return (
    <span
      data-semantic-color="verification"
      className={cn(
        "inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs",
        tone === "ok" && "border-success/20 bg-success/10 text-success",
        tone === "warn" && "border-amber-200 bg-amber-50 text-amber-700",
        tone === "neutral" && "border-border bg-muted/40 text-muted-foreground",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="font-medium">{label}</span>
      <span>{value}</span>
    </span>
  );
}

const BEHAVIOR_NODE_VOCABULARY = [
  { id: "trigger", icon: Zap },
  { id: "action", icon: MousePointer2 },
  { id: "conditional", icon: GitBranch },
  { id: "loop", icon: Repeat },
  { id: "switch", icon: GitFork },
  { id: "parallel", icon: Layers },
  { id: "tryCatch", icon: ShieldCheck },
  { id: "stage", icon: CheckCircle2 },
  { id: "saga", icon: GitBranch },
] as const;

/** Resolve a summary-derived flow id onto the live FlowMeta id. With
 *  id-preserving hydration a created (`flow_<ts>`) or renamed flow keeps its
 *  original id while the summary derives `<block>:<name>` — selectFlow must
 *  target the LIVE id (flowSteps routing and flowDataMap are keyed by it). */
function resolveLiveFlowId(summaryId: string, name: string): string {
  const { flows } = useFlowEditorStore.getState();
  if (flows.some((flow) => flow.id === summaryId)) return summaryId;
  const live = flows.find(
    (flow) =>
      flowNamesMatch(flow.name, name) || flowNamesMatch(name, flow.name),
  );
  return live?.id ?? summaryId;
}

function selectFlowFromBehavior(
  event: BehaviorEventEntry,
  summary: BehaviorSummary,
) {
  const store = useFlowEditorStore.getState();
  const flow = summary.flows.find(
    (entry) =>
      entry.name.toLowerCase() === event.flowName.toLowerCase() ||
      entry.name.toLowerCase().endsWith(`.${event.flowName.toLowerCase()}`),
  );

  store.setHighlightedEventId(event.id);
  store.setHighlightedComponentId(event.elementId);
  if (event.bindingKind === "action") {
    store.setSelectedEventContext({
      blockName: event.blockName,
      elementId: event.elementId,
      eventType: event.eventType,
    });
    return;
  }
  if (!flow) {
    store.setSelectedEventContext({
      blockName: event.blockName,
      elementId: event.elementId,
      eventType: event.eventType,
    });
    return;
  }

  const liveId = resolveLiveFlowId(flow.id, flow.name);
  store.selectFlow(liveId);
  store.setHighlightedFlowId(liveId);
}

/**
 * ActionFlow surface — event bindings and ActionFlow authoring.
 */
export function BehaviorSurface() {
  const { locale, strings } = useStudioLocale();
  const t = strings.behavior;
  const dataCopy = getDataSurfaceCopy(locale);
  const nodeDisplayName: Record<(typeof BEHAVIOR_NODE_VOCABULARY)[number]["id"], string> = {
    trigger: strings.dataInspector.fields.trigger,
    action: t.actionLane,
    conditional: t.palette.ifElse,
    loop: t.palette.loop,
    switch: t.palette.switch,
    parallel: t.palette.parallel,
    tryCatch: t.palette.tryCatch,
    stage: t.stage,
    saga: t.saga,
  };
  const project = useStudioStore((s) => s.currentProject);
  const projectId = useStudioStore((s) => s.currentProjectId);
  const selectedIds = useEditorStore((s) => s.selectedIds);
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName);
  const selectionTier = useEditorStore((s) => s.selectionTier);
  const selection = useStudioSelectionContext();
  const selectedFlowId = useFlowEditorStore((s) => s.selectedFlowId);
  const [showCreateFlow, setShowCreateFlow] = useState(false);

  const summary = useMemo(() => collectBehaviorSummary(project), [project]);
  useHydrateBehaviorFlows(projectId, summary);
  const editorFlows = useFlowEditorStore((s) => s.flows);
  // Resolve the selected flow against persisted truth: by id first, then by
  // the ownership name rule — a selected editor draft whose saves route into
  // a block's bare declaration IS that persisted flow, and must not render
  // as "(unsaved)" while the pill (same rule) counts it as saved.
  const selectedFlow = useMemo(() => {
    if (!selectedFlowId) return null;
    const byId = summary.flows.find((flow) => flow.id === selectedFlowId);
    if (byId) return byId;
    const editorMeta = editorFlows.find((flow) => flow.id === selectedFlowId);
    if (!editorMeta) return null;
    return (
      summary.flows.find((flow) =>
        flowNamesMatch(editorMeta.name, flow.name),
      ) ?? null
    );
  }, [editorFlows, selectedFlowId, summary.flows]);
  // Flows that live only in the flow-editor store — created via New Flow but
  // not yet persisted into any block. Counting only persisted flows made a
  // freshly created flow invisible ("0 flows" while the list shows one,
  // QA-5); counting the buffer as persisted would lie. Annotate instead.
  // "Persisted" uses the same full-then-bare name rule the bridge routes
  // saves with (flowNamesMatch) — a namespaced editor flow whose saves land
  // under the block's bare declaration must not be annotated unsaved forever.
  const unsavedFlowCount = useMemo(
    () =>
      editorFlows.filter(
        (flow) =>
          !summary.flows.some((persisted) =>
            flowNamesMatch(flow.name, persisted.name),
          ),
      ).length,
    [editorFlows, summary.flows],
  );
  const flowCountLabel =
    unsavedFlowCount > 0
      ? `${dataCopy.statusFlows(summary.flows.length)} · ${unsavedFlowCount} ${t.unsaved}`
      : dataCopy.statusFlows(summary.flows.length);
  const selectedEditorFlow = useMemo(
    () => editorFlows.find((flow) => flow.id === selectedFlowId) ?? null,
    [editorFlows, selectedFlowId],
  );
  // Verification status for the selected flow, derived from the latest
  // matching preview ActionFlow trace run (pass/error → Verified/Failed;
  // no matching trace → Not verified).
  const actionFlowTraces = usePreviewTestingStore((s) => s.actionFlowTraces);
  const selectedFlowName =
    selectedFlow?.name ?? selectedEditorFlow?.name ?? null;
  const verificationStatus = useMemo(
    () =>
      selectedFlowName
        ? deriveFlowVerification(selectedFlowName, actionFlowTraces)
        : "not_run",
    [selectedFlowName, actionFlowTraces],
  );
  const blocklessProject = Object.keys(project?.blocks ?? {}).length === 0;

  const selectedElementId =
    selectionTier === "component" && selectedIds.length === 1
      ? selectedIds[0]
      : null;
  const selectedEventKey =
    selectedElementId && selectedBlockName
      ? `${selectedBlockName}:${selectedElementId}`
      : null;
  const autoSelectedEventKeyRef = useRef<string | null>(null);
  const autoSelectedProjectRef = useRef<string | null>(null);
  const selectedBlock = selectedBlockName
    ? project?.blocks?.[selectedBlockName]
    : null;
  const selectedElement =
    selectedBlock?.ui?.root && selectedElementId
      ? findElementInTree(selectedBlock.ui.root, selectedElementId)
      : null;
  const selectedEvents = useMemo(
    () =>
      selectedElementId && selectedBlockName
        ? summary.events.filter(
            (event) =>
              event.blockName === selectedBlockName &&
              event.elementId === selectedElementId,
          )
        : [],
    [selectedBlockName, selectedElementId, summary.events],
  );
  useEffect(() => {
    if (!selectedEventKey) {
      autoSelectedEventKeyRef.current = null;
      return;
    }
    if (
      autoSelectedEventKeyRef.current === selectedEventKey ||
      selectedEvents.length === 0
    )
      return;
    const currentFlowStillConnected = selectedFlowName
      ? selectedEvents.some(
          (event) =>
            event.bindingKind === "flow" &&
            flowNamesMatch(event.flowName, selectedFlowName),
        )
      : false;
    autoSelectedEventKeyRef.current = selectedEventKey;
    if (currentFlowStillConnected) return;
    const firstBoundEvent =
      selectedEvents.find(
        (event) => event.bindingKind === "flow" && event.flowName,
      ) ?? selectedEvents[0];
    selectFlowFromBehavior(firstBoundEvent, summary);
  }, [selectedEventKey, selectedEvents, selectedFlowName, summary]);

  useEffect(() => {
    if (!projectId) {
      autoSelectedProjectRef.current = null;
      return;
    }
    if (
      autoSelectedProjectRef.current === projectId ||
      selectedFlowId ||
      editorFlows.length === 0
    ) {
      return;
    }

    const firstBoundEvent = summary.events.find(
      (event) => event.bindingKind === "flow" && event.flowName,
    );
    if (firstBoundEvent) {
      selectFlowFromBehavior(firstBoundEvent, summary);
    } else if (summary.flows[0]) {
      useFlowEditorStore
        .getState()
        .selectFlow(
          resolveLiveFlowId(summary.flows[0].id, summary.flows[0].name),
        );
    }
    autoSelectedProjectRef.current = projectId;
  }, [editorFlows.length, projectId, selectedFlowId, summary]);

  useEffect(() => {
    const store = useStudioStore.getState();
    const current = store.chatContext;
    if (
      current.surfaceId === "behavior" &&
      current.flowId === (selectedFlowId ?? undefined) &&
      current.flowName === (selectedFlowName ?? undefined) &&
      current.selectedElementId === (selectedElementId ?? undefined)
    ) {
      return;
    }
    store.setChatContext({
      ...current,
      surfaceId: "behavior",
      flowId: selectedFlowId ?? undefined,
      flowName: selectedFlowName ?? undefined,
      selectedBlockName: selectedBlockName ?? undefined,
      selectedElementId: selectedElementId ?? undefined,
      selectedElementLabel: selection?.label,
      selectedElementType: selectedElement?.type,
    });
  }, [
    selectedBlockName,
    selectedElement?.type,
    selectedElementId,
    selectedFlowId,
    selectedFlowName,
    selection?.label,
  ]);

  const behaviorChatContext = (operation: string) =>
    [
      `Studio behavior surface ActionFlow operation=${operation}`,
      selectedElementId ? `Selected element=${selectedElementId}` : null,
      selectedElement?.type ? `Selected type=${selectedElement.type}` : null,
      selectedBlockName ? `Selected block=${selectedBlockName}` : null,
      selectedFlowName ? `Selected flow=${selectedFlowName}` : null,
      `Bindings=${summary.events.length}`,
      `Selected element bindings=${selectedEvents.length}`,
      `Flows=${summary.flows.length}`,
      `Unsaved flows=${unsavedFlowCount}`,
      `Verification=${verificationStatus}`,
    ]
      .filter(Boolean)
      .join("; ");

  const actionFlowSubtitle = selectedFlow
    ? `${selectedFlow.blockName ?? "project"}:${selectedFlow.name}`
    : selectedEditorFlow
      ? `${selectedEditorFlow.name} (${t.unsaved})`
      : t.selectFlow;

  const flowContextPatch = {
    surfaceId: "behavior",
    ...(selectedFlowId ? { flowId: selectedFlowId } : {}),
    ...(selectedFlowName ? { flowName: selectedFlowName } : {}),
    ...(selectedBlockName ? { selectedBlockName } : {}),
    ...(selectedElementId ? { selectedElementId } : {}),
    ...(selection?.label ? { selectedElementLabel: selection.label } : {}),
    ...(selectedElement?.type
      ? { selectedElementType: selectedElement.type }
      : {}),
  };

  return (
    <StudioEditorHost
      testId="behavior-editor-host"
      navigatorLabel={t.navigation}
      navigatorWidth={260}
      toolbar={
        <StudioEditorToolbar
          title={t.title}
          subtitle={actionFlowSubtitle}
          actions={
            <>
              {selectedFlowName && (
                <VerificationStatusBadge status={verificationStatus} />
              )}
              <AskCogoSurfaceButton
                label="Review behavior"
                operationId="behavior.surface.review"
                operationKind="actionflow"
                chatContextPatch={flowContextPatch}
                context={() =>
                  [
                    "Studio behavior surface: review UI event bindings and ActionFlow.",
                    selectedElementId
                      ? `Selected element=${selectedElementId}`
                      : null,
                    selectedFlowName
                      ? `Selected flow=${selectedFlowName}`
                      : null,
                    `Bindings=${summary.events.length}`,
                    `Flows=${summary.flows.length}`,
                    "Chat operations=review flow, add step, test flow",
                  ]
                    .filter(Boolean)
                    .join("; ")
                }
              />
            </>
          }
        />
      }
      navigator={
        <div className="flex h-full min-h-0 flex-col overflow-y-auto p-3">
          <section>
            <h3 className="px-1 text-[11px] font-medium text-muted-foreground">
              {t.target}
            </h3>
            <div className="mt-2 rounded-md border border-border bg-background p-2.5">
              <div className="flex min-w-0 items-center gap-2">
                <MousePointer2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate text-xs font-semibold text-foreground">
                  {selection?.label ??
                    selectedElementId ??
                    selectedBlockName ??
                    t.noElement}
                </span>
              </div>
              <div className="mt-1.5 truncate font-mono text-[11px] text-muted-foreground">
                {selectedElement?.type ??
                  selection?.detail ??
                  t.selectInPreview}
              </div>
              {selectedBlockName && (
                <div className="mt-1 truncate text-[11px] text-muted-foreground">
                  {t.section} {selectedBlockName}
                </div>
              )}
            </div>
          </section>

          <section className="mt-4 border-t border-border pt-4">
            <h3 className="px-1 text-[11px] font-medium text-muted-foreground">
              {t.status}
            </h3>
            <div className="mt-2 flex flex-col gap-1.5">
              <StatusPill
                label={t.bindings}
                value={`${summary.events.length}`}
                tone={summary.orphanCount > 0 ? "warn" : "ok"}
              />
              <StatusPill
                label={t.flows}
                value={flowCountLabel}
                tone={
                  summary.emptyFlowCount > 0 || unsavedFlowCount > 0
                    ? "warn"
                    : "ok"
                }
              />
            </div>
          </section>

          <section className="mt-4 border-t border-border pt-4">
            <div className="flex items-center justify-between gap-2 px-1">
              <h3 className="text-[11px] font-medium text-muted-foreground">
                {t.flows}
              </h3>
              <span className="text-[10px] text-muted-foreground">
                {summary.flows.length} · {t.empty} {summary.emptyFlowCount}
              </span>
            </div>
            <div className="mt-2 space-y-1.5" data-testid="behavior-flow-list">
              {summary.flows.length > 0 ? (
                summary.flows.map((flow) => {
                  const liveId = resolveLiveFlowId(flow.id, flow.name);
                  const execution = summarizeActionFlowExecution(
                    flow.flow.steps ?? [],
                  );
                  const status = deriveFlowVerification(
                    flow.name,
                    actionFlowTraces,
                  );
                  const empty = (flow.flow.steps?.length ?? 0) === 0;
                  return (
                    <button
                      key={flow.id}
                      type="button"
                      aria-pressed={selectedFlowId === liveId}
                      onClick={() =>
                        useFlowEditorStore.getState().selectFlow(liveId)
                      }
                      className="w-full rounded-lg border border-border bg-background px-2.5 py-2 text-left transition-colors hover:bg-muted/40 aria-pressed:border-sky-300 aria-pressed:bg-sky-50"
                    >
                      <span className="flex items-center gap-2 text-xs font-semibold text-foreground">
                        <span className="min-w-0 flex-1 truncate">
                          {flow.name}
                        </span>
                        <span
                          className={cn(
                            "shrink-0 text-[10px] font-medium",
                            empty
                              ? "text-amber-700"
                              : status === "pass"
                                ? "text-success"
                                : "text-muted-foreground",
                          )}
                        >
                          {empty
                            ? `0 ${t.steps}`
                            : status === "pass"
                              ? t.verified
                              : status === "fail"
                                ? t.failed
                                : t.notVerified}
                        </span>
                      </span>
                      <span className="mt-1 block font-mono text-[10px] text-muted-foreground">
                        {flow.trigger} · {flow.flow.steps?.length ?? 0} {t.steps}
                      </span>
                      {(execution.stageCount > 0 ||
                        execution.sagaCount > 0 ||
                        execution.completionBarrierCount > 0) && (
                        <span
                          data-testid="behavior-flow-execution-summary"
                          className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[9px] text-muted-foreground"
                        >
                          {execution.stageCount > 0 && (
                            <span>{execution.stageCount} {t.stage}</span>
                          )}
                          {execution.sagaCount > 0 && (
                            <span>{execution.sagaCount} {t.saga}</span>
                          )}
                          {execution.completionBarrierCount > 0 && (
                            <span>{execution.completionBarrierCount} {t.completionBarrier}</span>
                          )}
                        </span>
                      )}
                    </button>
                  );
                })
              ) : (
                <p className="px-1 text-xs leading-5 text-muted-foreground">
                  {t.noFlows}
                </p>
              )}
            </div>
          </section>

          <section className="mt-4 border-t border-border pt-4">
            <h3 className="px-1 text-[11px] font-medium text-muted-foreground">
              {t.eventBindings}
            </h3>
            <p className="mt-1 px-1 text-[10px] text-muted-foreground">
              {t.connected} {summary.events.length - summary.orphanCount} · {t.orphan}{" "}
              {summary.orphanCount}
            </p>
            <div className="mt-2 space-y-1.5">
              {!selectedElementId ? (
                <p className="px-1 pb-1 text-xs leading-5 text-muted-foreground">
                  {t.inspectElement}
                </p>
              ) : null}
              {summary.events.length > 0 ? (
                summary.events.map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    className={cn(
                      "flex h-8 w-full min-w-0 items-center gap-1.5 rounded-md border px-2 text-xs transition-colors",
                      event.status === "orphan" ||
                        (!event.flowName && !event.actionName)
                        ? "border-amber-200 bg-amber-50 text-amber-800"
                        : "border-border bg-background text-foreground hover:bg-accent",
                    )}
                    onClick={() => selectFlowFromBehavior(event, summary)}
                    title={`${event.elementId}:${event.eventType} -> ${event.flowName || event.actionName || "unbound"}`}
                  >
                    <Zap className="h-3.5 w-3.5 shrink-0" />
                    <span className="shrink-0 font-medium">
                      {event.eventType}
                    </span>
                    <span className="text-muted-foreground">{"->"}</span>
                    <span className="min-w-0 flex-1 truncate text-left font-mono">
                      {event.flowName || event.actionName || "unbound"}
                    </span>
                    <span className="shrink-0 text-[9px] font-medium uppercase text-muted-foreground">
                      {event.bindingKind === "action" ? t.direct : event.status}
                    </span>
                  </button>
                ))
              ) : (
                <p className="px-1 text-xs leading-5 text-muted-foreground">
                  {t.noBindings}
                </p>
              )}
            </div>
          </section>

          <section
            className="mt-auto border-t border-border pt-4"
            data-testid="behavior-chat-ops"
          >
            <h3 className="px-1 text-[11px] font-medium text-muted-foreground">
              {t.chatActions}
            </h3>
            <div className="mt-2 space-y-1.5">
              <AskCogoSurfaceButton
                className="w-full justify-start"
                label={t.reviewFlow}
                operationId="behavior.flow.review"
                operationKind="actionflow"
                chatContextPatch={flowContextPatch}
                context={() => behaviorChatContext("review-flow")}
              />
              <AskCogoSurfaceButton
                className="w-full justify-start"
                label={t.addStep}
                operationId="behavior.flow.add-step"
                operationKind="actionflow"
                chatContextPatch={{
                  ...flowContextPatch,
                  selectedPropertyKey: "flow.steps",
                }}
                context={() => behaviorChatContext("add-step")}
              />
              <AskCogoSurfaceButton
                className="w-full justify-start"
                label={t.connectEvent}
                operationId="behavior.flow.connect-selected"
                operationKind="actionflow"
                chatContextPatch={{
                  ...flowContextPatch,
                  selectedPropertyKey: "events.binding",
                }}
                context={() => behaviorChatContext("connect-selected")}
              />
              <AskCogoSurfaceButton
                className="w-full justify-start"
                label={t.testFlow}
                operationId="behavior.flow.test"
                operationKind="actionflow"
                chatContextPatch={{
                  ...flowContextPatch,
                  selectedPropertyKey: "flow.verification",
                }}
                context={() => behaviorChatContext("test-flow")}
              />
            </div>
          </section>
        </div>
      }
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex h-10 shrink-0 items-center justify-between gap-3 border-b border-border bg-muted/20 px-4 text-xs">
          <div className="flex min-w-0 items-center gap-2">
            <GitBranch className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="font-semibold text-foreground">
              {t.editor}
            </span>
            <span className="min-w-0 truncate font-mono text-muted-foreground">
              {selectedFlow
                ? `${selectedFlow.blockName ?? "project"}:${selectedFlow.name}`
                : selectedEditorFlow
                  ? `${selectedEditorFlow.name} (${t.unsaved})`
                  : t.selectFlow}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {blocklessProject && (
              <span
                data-testid="newflow-blockless-hint"
                className="text-muted-foreground"
              >
                {t.noBlocks}
              </span>
            )}
            <span className="text-muted-foreground">
              {selectedFlow
                ? `${selectedFlow.flow.steps?.length ?? 0} ${t.steps}`
                : selectedEditorFlow
                  ? t.unsavedFlow
                  : flowCountLabel}
            </span>
            <AskCogoSurfaceButton
              label={t.testRun}
              operationId="behavior.flow.test"
              operationKind="actionflow"
              chatContextPatch={{
                ...flowContextPatch,
                selectedPropertyKey: "flow.verification",
              }}
              context={() => behaviorChatContext("test-run")}
              className="max-[1399px]:w-auto max-[1399px]:px-2.5"
            />
            <button
              type="button"
              disabled={blocklessProject}
              onClick={() => {
                if (!blocklessProject) setShowCreateFlow((value) => !value);
              }}
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md border transition-colors",
                blocklessProject
                  ? "cursor-not-allowed border-border bg-muted text-muted-foreground opacity-60"
                  : showCreateFlow
                    ? "border-foreground/40 bg-muted text-foreground"
                    : "border-border bg-background text-foreground hover:bg-muted",
              )}
              aria-label={showCreateFlow ? t.closeFlowForm : t.newFlow}
              title={
                blocklessProject
                  ? t.addBlockFirst
                  : showCreateFlow
                    ? t.closeFlowForm
                    : t.newFlow
              }
            >
              {showCreateFlow ? (
                <X className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <Plus className="h-3.5 w-3.5" aria-hidden />
              )}
            </button>
          </div>
        </div>
        {showCreateFlow && !blocklessProject && (
          <CreateFlowForm compact onClose={() => setShowCreateFlow(false)} />
        )}
        <div
          className="flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-border bg-background px-3 py-2"
          data-testid="behavior-node-vocabulary"
          aria-label={t.nodeTypes}
        >
          <span className="mr-1 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t.nodes}
          </span>
          {BEHAVIOR_NODE_VOCABULARY.map((node) => {
            const Icon = node.icon;
            return (
              <span
                key={node.id}
                className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-2.5 text-[10px] font-medium text-foreground"
              >
                <Icon className="h-3 w-3 text-sky-600" aria-hidden />
                {nodeDisplayName[node.id]}
              </span>
            );
          })}
        </div>
        <div
          data-testid="behavior-execution-model"
          className="flex shrink-0 items-center gap-2 overflow-x-auto border-b border-border bg-muted/20 px-3 py-2 text-[10px]"
          aria-label={t.executionModel}
        >
          <span className="shrink-0 font-semibold uppercase tracking-wide text-muted-foreground">
            {t.executionModel}
          </span>
          <span className="shrink-0 rounded-md border border-border bg-background px-2 py-1 text-foreground">
            {t.binding}: <strong>{t.directAtomic}</strong> / {t.namedFlow}
          </span>
          <span className="shrink-0 rounded-md border border-border bg-background px-2 py-1 text-foreground">
            {t.lanes}: <strong>{t.actionLane}</strong> / {t.uiLane} / {t.motionLane}
          </span>
          <span className="shrink-0 rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300">
            {t.order}: {t.completionBarrier}
          </span>
          <span className="shrink-0 text-muted-foreground">
            {t.sagaCompensation}
          </span>
        </div>
        <EditorShell embedded />
      </div>
    </StudioEditorHost>
  );
}
